'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../../../../ui/button';
import { Label } from '../../../../ui/label';
import CustomTextInput from '../../../../resuable-ui/text-input';
import CustomTextarea from '../../../../resuable-ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '../../../../ui/dialog';
import { useCartAnalytics } from '../../../../../hooks/useCartAnalytics';
import { useMarkCartWithTransactionID, usePayment } from '../../../../../integrations/react_query/mutations';
import { useGetCart } from '../../../../../integrations/react_query/queries';
import { useCompleteSales } from '../../../../../hooks/useCompleteSales';
import { useSubmitGuard } from '../../../../../hooks/useSubmitGuard';
import { EQuerykeys, ECart } from '../../../../../models/index';
import { handlePayment, calculateBalanceDue } from '../utils/paymentUtils';
import { PaymentTypes } from '../../../../../types/index';

type PaymentMethod = {
	label: string;
	id: string;
	value: string;
};

interface CashPaymentConfirmationProps {
	title?: string;
	methods: PaymentMethod[];
	defaultAmount: string | number;
	onConfirm: (data: PaymentConfirmationForm) => void;
	onClear?: () => void;
	showConfirmationModal: boolean;
	setShowConfirmationModal: (show: boolean) => void;
	paymentType: string;
}

const PaymentConfirmationSchema = z.object({
	amount: z.coerce.number().min(1, 'Amount must be at least 1'),
	note: z.string().max(100, 'Note must be at most 100 characters').optional(),
});

type PaymentConfirmationForm = z.infer<typeof PaymentConfirmationSchema>;

const CashCreditPaymentConfirmation: React.FC<CashPaymentConfirmationProps> = ({
	title = 'Card Payment Confirmation',
	defaultAmount,
	onClear,
	showConfirmationModal,
	setShowConfirmationModal,
	paymentType,
}) => {
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { isSubmitting },
	} = useForm<PaymentConfirmationForm, typeof PaymentConfirmationSchema>({
		resolver: zodResolver(PaymentConfirmationSchema) as any,
		defaultValues: {
			amount: 0,
			note: '',
		},
	});

	const { data: cartItems, isLoading } = useGetCart([EQuerykeys.cart, ECart.active]);
	const { completeTransaction, transactionId } = useCompleteSales();
	const { mutateAsync: markCartWithTransactionID } = useMarkCartWithTransactionID();
	const { mutateAsync: makePayment } = usePayment();

	const { payableAmount } = useCartAnalytics(cartItems?.products);

	const [showOverpayModal, setShowOverpayModal] = React.useState(false);
	const [pendingData, setPendingData] = React.useState<PaymentConfirmationForm | null>(null);

	const { isSubmitting: isConfirmingOverpay, runGuarded: runOverpayConfirm } = useSubmitGuard();

	const onSubmit = async (data: PaymentConfirmationForm) => {
		if (!cartItems) return;

		const balanceDueAfter = calculateBalanceDue(+payableAmount, cartItems.payments, Number(data.amount));

		if (balanceDueAfter <= 0) {
			setPendingData({ ...data });
			setShowOverpayModal(true);
			return;
		}

		await handlePayment({
			amountPaid: data.amount,
			note: data.note,
			paymentType: paymentType as PaymentTypes,
			paymentsHistory: cartItems.payments,
			payableAmount: +payableAmount,
			transactionId,
			onPayment: makePayment as unknown as any,
			markCartWithTransactionID,
			completeTransaction,
			onSuccess: () => {
				setShowConfirmationModal(false);
			},
			paymentProvider: undefined,
		});
	};

	const noteValue = watch('note') || '';
	const amountValue = watch('amount') || 0;

	const balanceDue = React.useMemo(() => {
		if (!cartItems) return 0;
		return calculateBalanceDue(+payableAmount, cartItems.payments, Number(amountValue));
	}, [cartItems, amountValue, payableAmount]);

	if (isLoading) {
		return <div>Loading...</div>;
	}
	if (!cartItems) {
		return <div>No available data</div>;
	}

	return (
		<>
			{showOverpayModal && (
				<Dialog open={showOverpayModal} onOpenChange={setShowOverpayModal}>
					<DialogContent className="bg-white p-6 max-w-md text-center">
						<DialogTitle className="text-lg font-semibold">Confirm Payment</DialogTitle>
						<p className="mt-4 text-gray-700">
							The amount for the cart items has been
							{balanceDue < 0 ? ' overpaid.' : ' fully paid.'} <br />
							Click "Yes" to proceed if you confirm this.
						</p>
						<p className="mt-2 font-bold text-sm text-gray-600">Note: The sale will be recorded automatically.</p>

						<div className="flex flex-row justify-center gap-4 mt-6">
							<Button
								className="max-w-fit border border-[#E2E8F0] bg-[#FEFBFF] py-4 text-[#718096]"
								type="button"
								variant="outline"
								onClick={() => setShowOverpayModal(false)}
							>
								No
							</Button>
							<Button
								className='className="flex-1 bg-[#A63494] py-4 font-semibold text-xl text-center text-white'
								type="button"
								disabled={isConfirmingOverpay}
								onClick={() => {
									if (!pendingData || !cartItems) return;

									void runOverpayConfirm(() =>
										handlePayment({
											amountPaid: pendingData.amount,
											note: pendingData.note,
											paymentType: paymentType as PaymentTypes,
											paymentsHistory: cartItems.payments,
											payableAmount: +payableAmount,
											transactionId,
											onPayment: makePayment as unknown as any,
											markCartWithTransactionID,
											completeTransaction,
											onSuccess: () => {
												setShowConfirmationModal(false);
											},
											paymentProvider: undefined,
										})
									);
								}}
							>
								Yes
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}

			{/* Desktop Modal */}
			<Dialog
				open={showConfirmationModal && window.innerWidth >= 768}
				onOpenChange={(v) => setShowConfirmationModal(v)}
			>
				<DialogContent className="!min-w-[750px] bg-white px-3 gap-0 sm:w-full">
					<DialogTitle className="flex items-center">
						<h3 className="text-lg uppercase text-[20px] font-[700]">{title}</h3>
					</DialogTitle>

					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="grid md:grid-cols-2 gap-6 mt-3">
							<div>
								<div className="flex items-center justify-between mb-2 text-[#353535] font-[600] text-[12px]">
									<Label htmlFor="amount">
										Balance due -{' '}
										<span className={balanceDue < 0 ? 'text-red-500' : balanceDue === 0 ? 'text-green-600' : ''}>
											₦{Math.abs(balanceDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
											{balanceDue < 0 && ' (Overpaid)'}
										</span>
									</Label>
								</div>

								<Controller
									name="amount"
									control={control}
									render={({ field, fieldState }) => (
										<CustomTextInput
											id="amount"
											{...field}
											onChange={(e) => field.onChange(e.target.value)}
											className="mt-1 h-[45px]"
											placeholder={paymentType === PaymentTypes.credit ? 'Enter credit amount' : 'Enter amount paid'}
											error={fieldState.error}
										/>
									)}
								/>
							</div>

							<div>
								<div className="flex items-center justify-between mb-2 text-[#353535] font-[600] text-[12px]">
									<Label htmlFor="amount">Total - ₦{Number(defaultAmount).toLocaleString()}</Label>
								</div>

								<Button
									disabled={balanceDue <= 0}
									onClick={() => {
										if (balanceDue > 0) {
											setValue('amount', balanceDue + +amountValue);
										}
									}}
									type="button"
									className="w-full font-semibold text-xl text-center bg-[#A63494] text-white py-6 rounded"
								>
									₦ {Number(balanceDue + +amountValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
								</Button>
							</div>
						</div>

						<div className="mt-4">
							<Label className="mb-2" htmlFor="note">
								Note (Optional)
							</Label>
							<Controller
								name="note"
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextarea
										label=""
										id="note"
										{...field}
										placeholder="Write a note..."
										maxLength={100}
										error={fieldState.error}
										rows={5}
									/>
								)}
							/>
							<div className="text-right text-xs text-muted-foreground">{noteValue.length}/100</div>
						</div>

						<div className="grid md:grid-cols-2 gap-6 mt-6">
							<Button
								className="max-w-fit border border-[#E2E8F0] bg-[#FEFBFF] py-6 px-6 text-[#718096]"
								type="button"
								variant="outline"
								onClick={onClear}
							>
								Clear
							</Button>

							<Button
								className="flex-1 bg-[#A63494] py-6 font-semibold text-xl text-center text-white"
								type="submit"
								disabled={isSubmitting}
							>
								OK
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>

			{/* Mobile Modal Content */}
			{!showConfirmationModal ? null : (
				<form onSubmit={handleSubmit(onSubmit)} className="md:hidden px-4 pb-6">
					<div className="space-y-5">
						{/* Amount Section */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<Label htmlFor="amount" className="text-[#718096] text-[12px]">
									Balance due
								</Label>
								<span
									className={`text-[12px] font-semibold ${balanceDue < 0 ? 'text-red-500' : balanceDue === 0 ? 'text-green-600' : 'text-gray-600'}`}
								>
									₦{Math.abs(balanceDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
									{balanceDue < 0 && ' (Overpaid)'}
								</span>
							</div>

							<div className="flex items-center justify-between text-[11px] text-gray-500 mb-2">
								<span>Total to pay</span>
								<span className="font-semibold text-gray-700">₦{Number(defaultAmount).toLocaleString()}</span>
							</div>

							<Controller
								name="amount"
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextInput
										id="amount"
										{...field}
										onChange={(e) => field.onChange(e.target.value)}
										className="mt-1 h-[44px] text-sm"
										placeholder={paymentType === PaymentTypes.credit ? 'Enter credit amount' : 'Enter amount paid'}
										error={fieldState.error}
									/>
								)}
							/>

							<Button
								disabled={balanceDue <= 0}
								onClick={() => {
									if (balanceDue > 0) {
										setValue('amount', balanceDue + +amountValue);
									}
								}}
								type="button"
								className="w-full font-semibold text-sm text-center bg-[#A63494] text-white py-3 rounded"
							>
								₦ {Number(balanceDue + +amountValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
							</Button>
						</div>

						{/* Note Section */}
						<div className="space-y-2 border-t pt-4">
							<Label className="text-[12px] text-gray-700 font-semibold" htmlFor="note">
								Note (Optional)
							</Label>
							<Controller
								name="note"
								control={control}
								render={({ field, fieldState }) => (
									<CustomTextarea
										label=""
										id="note"
										{...field}
										placeholder="Write a note..."
										maxLength={100}
										error={fieldState.error}
										rows={3}
										className="text-sm"
									/>
								)}
							/>
							<div className="text-right text-xs text-gray-500">{noteValue.length}/100</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="grid grid-cols-2 gap-3 mt-6">
						<Button
							className="border border-[#E2E8F0] bg-[#FEFBFF] py-3 px-4 text-[#718096] text-sm font-medium"
							type="button"
							variant="outline"
							onClick={onClear}
						>
							Clear
						</Button>

						<Button
							className="bg-[#A63494] py-3 px-4 font-semibold text-sm text-center text-white rounded"
							type="submit"
							disabled={isSubmitting}
						>
							OK
						</Button>
					</div>
				</form>
			)}
		</>
	);
};

export default CashCreditPaymentConfirmation;
